/**
 * monthlyReportModel — the SINGLE shared FE selector for every rupee figure
 * on the Collections board (IPD-MVP discharge-mis, BUILD-PLAN
 * fast-slice-v2 §8 WAVE 2 F2, binding to §7 FROZEN API CONTRACT).
 *
 * Every surface that renders a money figure from `GET
 * /organisation/reports/monthly-collections` or `GET
 * /organisation/reports/discharges` (today: `IpdRevenue`, `DischargeRegister`;
 * later: the export button's sheet list, the OPD block) reads it through
 * THIS file — never inline. Two hard rules the whole board depends on:
 *
 *   1. Money is passed through VERBATIM. The server already computed the
 *      six-term identity (B1, with the [X4] write-off correction baked
 *      into `still_outstanding_paise`) — this file NEVER re-sums a total
 *      from its parts. Re-deriving it here would silently mask a backend
 *      regression instead of surfacing it (C1/C8's whole point).
 *   2. `insurance_settled_paise` is returned by a SEPARATE selector so no
 *      caller can accidentally fold it into a cash total — it is a
 *      non-cash accrual (C1), never summed with the cash terms.
 */

import { formatPaise } from '../../utils/money';
import { STAFF_ROLES } from '../../constants/staff';

export { formatPaise };

/**
 * The five CASH/loss terms of the six-term IPD identity, plus the
 * concessions memo — straight off `data.ipd`, each figure formatted for
 * display alongside its raw paise value. `insuranceSettled` is deliberately
 * NOT included here (see `selectInsuranceSettled`) so it can never be
 * summed alongside these by a careless caller.
 */
export function selectIpdRevenueTerms(ipd) {
  if (!ipd) return null;
  return {
    basis: ipd.basis,
    netBilledPaise: ipd.net_billed_paise,
    netBilled: formatPaise(ipd.net_billed_paise),
    depositsAdjustedPaise: ipd.deposits_adjusted_paise,
    depositsAdjusted: formatPaise(ipd.deposits_adjusted_paise),
    duesCollectedPaise: ipd.dues_collected_paise,
    duesCollected: formatPaise(ipd.dues_collected_paise),
    writtenOffPaise: ipd.written_off_paise,
    writtenOff: formatPaise(ipd.written_off_paise),
    // [X4] — already net of write-offs on the wire; never re-derived here.
    stillOutstandingPaise: ipd.still_outstanding_paise,
    stillOutstanding: formatPaise(ipd.still_outstanding_paise),
    concessionsPaise: ipd.concessions_paise,
    concessions: formatPaise(ipd.concessions_paise),
  };
}

/** `insurance_settled_paise` — non-cash accrual, kept apart from every cash term (C1). */
export function selectInsuranceSettled(ipd) {
  if (!ipd) return null;
  return {
    insuranceSettledPaise: ipd.insurance_settled_paise,
    insuranceSettled: formatPaise(ipd.insurance_settled_paise),
  };
}

/** Discharge counts by type, straight off `data.ipd.discharges` ([X2] — the 5 DischargeType members + "UNSET"). */
export function selectDischargeCounts(ipd) {
  if (!ipd?.discharges) return null;
  return {
    total: ipd.discharges.total,
    byType: ipd.discharges.by_type || {},
  };
}

/**
 * §7.2 discharge-register row money, formatted through the SAME
 * `formatPaise` every other figure on this board uses — so the register's
 * column totals equal §7.1's `ipd` block to the paisa (§7.2: "MUST equal
 * §7.1's ipd block, to the paisa — same selector"). `DischargeRegister`
 * (F4) reads money ONLY through this — never a raw `formatPaise(row.x)`
 * inline, and never a re-sum of the row into a page/column total.
 * Callers check `row.reconciled` themselves (F4) — an unreconciled row's
 * money cells must render BLANK, never a real ₹0 alongside this selector's
 * "computed" values, so this file deliberately does not paper over that.
 *
 * `concessionsPaise`/`concessions` (DOCTOR-REPORTS P1 scope-add) mirrors
 * `selectIpdRevenueTerms`'s `concessions_paise` memo at row grain — it is
 * already netted into `net_billed_paise` server-side, so it is a MEMO
 * column, never summed into the register's other terms.
 */
export function selectRegisterRowMoney(row) {
  if (!row) return null;
  return {
    netBilledPaise: row.net_billed_paise,
    netBilled: formatPaise(row.net_billed_paise),
    concessionsPaise: row.concessions_paise,
    concessions: formatPaise(row.concessions_paise),
    depositsAdjustedPaise: row.deposits_adjusted_paise,
    depositsAdjusted: formatPaise(row.deposits_adjusted_paise),
    duesCollectedPaise: row.dues_collected_paise,
    duesCollected: formatPaise(row.dues_collected_paise),
    insuranceSettledPaise: row.insurance_settled_paise,
    insuranceSettled: formatPaise(row.insurance_settled_paise),
    writtenOffPaise: row.written_off_paise,
    writtenOff: formatPaise(row.written_off_paise),
    // [X4] — already net of write-offs on the wire; never re-derived here.
    stillOutstandingPaise: row.still_outstanding_paise,
    stillOutstanding: formatPaise(row.still_outstanding_paise),
  };
}

/**
 * The two loud tripwires (§1 T1/T2) as one banner-ready shape. `truncated`
 * ⇒ export must refuse (409 REPORT_INCOMPLETE); non-empty `unreconciled` ⇒
 * export refuses (409 REPORT_UNRECONCILED). Read straight off
 * `data.coverage` — never re-derived.
 */
export function selectCoverageBanners(coverage) {
  const unreconciled = Array.isArray(coverage?.unreconciled) ? coverage.unreconciled : [];
  return {
    truncated: !!coverage?.truncated,
    scanned: coverage?.admissions_scanned,
    unreconciledCount: unreconciled.length,
    hasUnreconciled: unreconciled.length > 0,
  };
}

/**
 * MICROCOPY M14 — the T1 (truncated-coverage) tripwire sentence. Read by
 * BOTH `IpdRevenue` (the banner) and `ExportMonthButton` (the disabled
 * reason) so the two surfaces can never disagree on the wording.
 */
export function selectTruncatedMessage(scanned, label) {
  return `${label} is incomplete — only ${scanned} admissions could be read. `
    + 'The figures below are understated and this month cannot be exported.';
}

/**
 * MICROCOPY M15 — the T2 (unreconciled) tripwire sentence, singular/plural.
 * Read by BOTH `IpdRevenue` (the banner) and `ExportMonthButton` (the
 * disabled reason) so the two surfaces can never disagree on the wording.
 */
export function selectUnreconciledMessage(count, label) {
  const noun = count === 1 ? 'admission' : 'admissions';
  const pronoun = count === 1 ? 'Its' : 'Their';
  return `${count} ${noun} could not be reconciled. ${pronoun} amounts are shown blank `
    + `in the register below — blank means not known, not ₹0. ${label} cannot be exported.`;
}

/**
 * DOCTOR-REPORTS P1 (§2 FROZEN API CONTRACT `ipd.by_doctor` / register
 * `primary_doctor_*` fields). The single source for the doctor filter key,
 * label and bucket/row selection — every surface (`IpdRevenue`,
 * `DischargeRegister`, `ByDoctorTable`, the page's `DoctorPicker`) reads
 * doctor identity ONLY through this file, never an inline `|| 'Unassigned'`.
 */
export const UNASSIGNED_DOCTOR_KEY = '__unassigned__';
export const UNASSIGNED_LABEL = 'Unassigned';
export const UNKNOWN_DOCTOR_LABEL = 'Unknown doctor';

/** A wire `doctor_member_id` (present) or absent/null/'' → the Unassigned sentinel key. */
export function doctorKey(idOrNull) {
  return idOrNull ? idOrNull : UNASSIGNED_DOCTOR_KEY;
}

/** name if present; else "Unknown doctor" when an id exists with no name; else "Unassigned". */
export function doctorLabel(id, name) {
  if (name) return name;
  if (id) return UNKNOWN_DOCTOR_LABEL;
  return UNASSIGNED_LABEL;
}

/**
 * `ipd.by_doctor[]` → the same buckets, server order kept, each carrying its
 * `key`/`label`. `[]` when `by_doctor` is absent (old backend — every caller
 * hides the doctor UI on an empty array, never a crash on a missing key).
 */
export function selectDoctorBuckets(ipd) {
  if (!ipd || !Array.isArray(ipd.by_doctor)) return [];
  return ipd.by_doctor.map((bucket) => ({
    ...bucket,
    key: doctorKey(bucket.doctor_member_id),
    label: doctorLabel(bucket.doctor_member_id, bucket.doctor_name),
  }));
}

/**
 * The `DoctorPicker` doctor list — buckets WITH at least one discharge only
 * (D2: the always-present Unassigned bucket is offered in the filter only
 * when it has discharges), shaped to the picker's `{member_id, first_name,
 * last_name}` prop contract. `key` rides as `member_id` so `onChange`
 * already returns the right filter value.
 */
export function selectDoctorFilterOptions(ipd) {
  return selectDoctorBuckets(ipd)
    .filter((bucket) => bucket.discharges?.total > 0)
    .map((bucket) => ({ member_id: bucket.key, first_name: bucket.label, last_name: '' }));
}

/**
 * D3 — "Paid after discharge" (the renamed `dues_collected_paise` term)
 * shows only when the ORG month total is non-zero. Keyed to the ORG value
 * (never the filtered doctor's), so the tile/column/register-column appear
 * or disappear together and never flicker as the filter changes.
 */
export function selectShowPaidAfterDischarge(ipd) {
  return !!ipd && ipd.dues_collected_paise !== 0;
}

/** Register rows for a doctor filter key; `null`/falsy key returns every row. */
export function selectRegisterRowsForDoctor(rows, key) {
  if (!key) return rows || [];
  return (rows || []).filter((row) => doctorKey(row.primary_doctor_member_id) === key);
}

/**
 * The Backdated tag sentence (§2.2 `backdated`/`discharge_recorded_date`/
 * `discharge_recorded_by_name`) — `null` unless the row is backdated. The
 * actor name is optional on the wire (legacy/no-name actor), so the tag
 * degrades to "entered {date}" without inventing a name.
 */
export function selectBackdatedTag(row) {
  if (!row?.backdated) return null;
  const date = row.discharge_recorded_date;
  const name = row.discharge_recorded_by_name;
  return name ? `Backdated — entered ${date} by ${name}` : `Backdated — entered ${date}`;
}

/**
 * DOCTOR-REPORTS P2 (build-plan §API CONTRACT FROZEN — `opd`/`otc`/`lab`/
 * `cash_coverage`). Every cash-section figure (`CashCollectionsTab`,
 * `CashMemberTable`) reads through the selectors below — never inline. The
 * two hard rules from the header docstring still hold: money is passed
 * through VERBATIM (a section/member block's own terms are never re-summed
 * from its parts here — the server-side tie-out invariants I1-I4 own that),
 * and a section's footer always reads the BLOCK, never a client sum of rows.
 */

export const UNKNOWN_STAFF_LABEL = 'Unknown staff';

// The 7 `PaymentMethod` wire keys (backend `app/schemas/payment.py`) — kept
// as an explicit list so a drift (BE adds/removes a mode) fails the guard
// test in `monthlyReportModel.cash.test.js` instead of silently mis-folding.
export const CASH_PAYMENT_MODE_KEYS = ['CASH', 'CARD', 'UPI', 'QR', 'NETBANKING', 'WALLET', 'OTHER'];
const OTHER_MODE_KEYS = ['QR', 'NETBANKING', 'WALLET', 'OTHER'];

/** A cash-section member label — "Unknown doctor" (OPD) / "Unknown staff" (OTC, lab) per §API CONTRACT FE binding notes. */
export function cashMemberLabel(id, name, sectionKind) {
  if (name) return name;
  if (id) return (sectionKind === 'otc' || sectionKind === 'lab') ? UNKNOWN_STAFF_LABEL : UNKNOWN_DOCTOR_LABEL;
  return UNASSIGNED_LABEL;
}

/**
 * Recon.md item 18 — the ONE helper every "Unassigned"/"Unknown doctor"/
 * "Unknown staff" row is checked against before it renders, on all four
 * tables (`ByDoctorTable`, OPD/OTC/Lab-by-technician `CashMemberTable`s) and
 * the doctor-filter options: hidden ONLY when it is unattributed (its
 * `label` is one of the three sentinel labels — set by `doctorLabel` /
 * `cashMemberLabel`, the same identity every row already renders) AND every
 * one of its own money terms is zero — matched generically by field name
 * ending "paise" (case-insensitive: covers both a raw wire bucket's
 * `net_paise` and a shaped row's `netPaise`, absent counts as zero) — AND
 * (IPD only) its discharges total and unreconciled count are zero too.
 * A NEGATIVE term is non-zero — ALWAYS shown, never masked as if it were
 * empty. A named row is never unattributed, so it is never hidden by this
 * rule. Raw selectors (`selectDoctorBuckets`, `selectCashSection`) stay
 * UNFILTERED — only `selectVisibleRows` (render-only) applies this.
 */
export function isEmptyUnattributedRow(bucket) {
  if (!bucket) return false;
  const unattributed = bucket.label === UNASSIGNED_LABEL
    || bucket.label === UNKNOWN_DOCTOR_LABEL
    || bucket.label === UNKNOWN_STAFF_LABEL;
  if (!unattributed) return false;
  const moneyAllZero = Object.keys(bucket)
    .filter((k) => /paise$/i.test(k))
    .every((k) => (bucket[k] || 0) === 0);
  const dischargesZero = (bucket.discharges?.total || 0) === 0;
  const unreconciledZero = (bucket.unreconciled || 0) === 0;
  return moneyAllZero && dischargesZero && unreconciledZero;
}

/**
 * `rows` (already carrying `key`/`label`, i.e. `selectDoctorBuckets` /
 * `selectCashSection` output) with every all-zero Unassigned/Unknown row
 * dropped (recon item 18). Render-only — footers and raw selectors keep
 * reading the server block/full row list, never this filtered view.
 */
export function selectVisibleRows(rows) {
  return (rows || []).filter((row) => !isEmptyUnattributedRow(row));
}

// A `CashSectionBlock` and a `MemberBucket` (and a Lab `LabPart`, minus the
// kind split) share the same money field names — one formatter for both.
function cashTerms(source) {
  if (!source) return null;
  return {
    consultationPaise: source.consultation_paise ?? 0,
    consultation: formatPaise(source.consultation_paise ?? 0),
    proceduresPaise: source.procedures_paise ?? 0,
    procedures: formatPaise(source.procedures_paise ?? 0),
    referralPaise: source.referral_paise ?? 0,
    referral: formatPaise(source.referral_paise ?? 0),
    grossPaise: source.gross_paise,
    gross: formatPaise(source.gross_paise),
    discountsPaise: source.discounts_paise,
    discounts: formatPaise(source.discounts_paise),
    refundsPaise: source.refunds_paise,
    refunds: formatPaise(source.refunds_paise),
    netPaise: source.net_paise,
    net: formatPaise(source.net_paise),
  };
}

/**
 * `data.opd` / `data.otc` → `{ terms, rows }` — `terms` is the section's own
 * org-level waterfall (never FE-summed from `rows`); `rows` is `by_member`
 * in SERVER order (Unassigned already last on the wire) shaped with a
 * `key`/`label`/`isUnassigned`/`isNegative` for the table + filter to read.
 * `sectionKind` is `'opd'` or `'otc'` — only changes the nameless-id label.
 */
export function selectCashSection(block, sectionKind) {
  if (!block) return null;
  return {
    basis: block.basis,
    terms: cashTerms(block),
    rows: (block.by_member || []).map((bucket) => ({
      key: doctorKey(bucket.member_id),
      label: cashMemberLabel(bucket.member_id, bucket.member_name, sectionKind),
      role: bucket.member_role || null,
      isUnassigned: !bucket.member_id,
      isNegative: bucket.net_paise < 0,
      ...cashTerms(bucket),
    })),
  };
}

/** The one member's own bucket for a doctor-filter key — `null` if the key has no row in this section (D5's "no OPD/OTC collections for {doctor}" empty state). Never a re-sum. */
export function selectCashRowForKey(block, key) {
  if (!block || !key) return null;
  return (block.by_member || []).find((bucket) => doctorKey(bucket.member_id) === key) || null;
}

/**
 * `data.lab` → the two-source (OPD/IPD) org-level waterfall (`LabCashBlock`/
 * `LabPart`) — never filtered by doctor (ruling 17: lab is BY TECHNICIAN,
 * not by doctor). The per-technician split (`lab.by_member`) is read
 * separately through the SAME `selectCashSection(lab, 'lab')` every OPD/OTC
 * table already uses — no new selector (§API CONTRACT). `null` when the
 * key is absent (Lab card hidden).
 *
 * BUSY-VOUCHER R1: `data.radiology` has the EXACT same `LabCashBlock` shape,
 * so the Radiology card reads it through these SAME two selectors — there is
 * deliberately no `selectRadiologyCash`.
 */
export function selectLabCash(lab) {
  if (!lab) return null;
  return {
    basis: lab.basis,
    ...cashTerms(lab),
    opd: cashTerms(lab.by_source?.OPD),
    ipd: cashTerms(lab.by_source?.IPD),
  };
}

/**
 * `by_mode` (7 keys, always present) → the 4 display lines Cash/UPI/Card/
 * Other, QR+NETBANKING+WALLET+OTHER folded into "Other" (S9), zero lines
 * dropped. Reads the BLOCK's `by_mode` only — never recomputed per filtered
 * row, so the mode line stays organisation-level under a doctor filter.
 */
export function selectPaymentModeLine(byMode) {
  if (!byMode) return [];
  const other = OTHER_MODE_KEYS.reduce((sum, k) => sum + (byMode[k] || 0), 0);
  return [
    { key: 'CASH', label: 'Cash', paise: byMode.CASH || 0 },
    { key: 'UPI', label: 'UPI', paise: byMode.UPI || 0 },
    { key: 'CARD', label: 'Card', paise: byMode.CARD || 0 },
    { key: 'OTHER', label: 'Other', paise: other },
  ]
    .filter((line) => line.paise !== 0)
    .map((line) => ({ ...line, formatted: formatPaise(line.paise) }));
}

/** `cash_coverage.truncated` — the "figures may be understated" banner gate. */
export function selectCashTruncated(cashCoverage) {
  return !!cashCoverage?.truncated;
}

/**
 * D9 — the ONE Doctor filter's options, union of the P1 IPD list (server
 * order kept, so the P1 QA pins stay green) with doctors seen ONLY in the
 * cash blocks: every OPD member (OPD money is doctor-only by construction)
 * plus OTC members whose `member_role` is `STAFF_ROLES.DOCTOR` (nurses are
 * never offered). Named entries sort A-Z, nameless ("Unknown doctor")
 * entries by id, Unassigned always last — offered when the IPD Unassigned
 * bucket has discharges OR the OPD Unassigned bucket has any non-zero term
 * (an OTC-only Unassigned never adds the option — ruling: nurses/OTC never
 * drive it). Shape matches `DoctorPicker`'s `{member_id, first_name,
 * last_name}` contract, same as `selectDoctorFilterOptions`.
 */
export function selectUnifiedDoctorFilterOptions(data) {
  const ipdOptions = selectDoctorFilterOptions(data?.ipd);
  const seen = new Set(ipdOptions.map((o) => o.member_id));

  const named = new Map();
  const unknown = new Map();
  function collect(block, doctorsOnly) {
    (block?.by_member || []).forEach((bucket) => {
      if (!bucket.member_id) return; // Unassigned handled separately below
      if (doctorsOnly && bucket.member_role !== STAFF_ROLES.DOCTOR) return;
      if (seen.has(bucket.member_id) || named.has(bucket.member_id) || unknown.has(bucket.member_id)) return;
      if (bucket.member_name) { named.set(bucket.member_id, bucket.member_name); return; }
      // Item 18: an id-only bucket with every money term at zero never adds
      // an "Unknown doctor" option — the filter would offer a doctor with
      // nothing to show.
      if (isEmptyUnattributedRow({ ...bucket, label: doctorLabel(bucket.member_id, bucket.member_name) })) return;
      unknown.set(bucket.member_id, true);
    });
  }
  collect(data?.opd, false);
  collect(data?.otc, true);

  const namedOptions = [...named.entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([member_id, name]) => ({ member_id, first_name: name, last_name: '' }));
  const unknownOptions = [...unknown.keys()]
    .sort()
    .map((member_id) => ({ member_id, first_name: UNKNOWN_DOCTOR_LABEL, last_name: '' }));

  const ipdUnassigned = ipdOptions.find((o) => o.member_id === UNASSIGNED_DOCTOR_KEY) || null;
  const ipdRest = ipdOptions.filter((o) => o.member_id !== UNASSIGNED_DOCTOR_KEY);

  const opdUnassignedBucket = (data?.opd?.by_member || []).find((b) => !b.member_id);
  const opdUnassignedActive = !!opdUnassignedBucket && [
    'consultation_paise', 'procedures_paise', 'referral_paise', 'gross_paise', 'discounts_paise', 'refunds_paise', 'net_paise',
  ].some((f) => (opdUnassignedBucket[f] || 0) !== 0);

  const unassignedOption = ipdUnassigned
    || (opdUnassignedActive ? { member_id: UNASSIGNED_DOCTOR_KEY, first_name: UNASSIGNED_LABEL, last_name: '' } : null);

  return [...ipdRest, ...namedOptions, ...unknownOptions, ...(unassignedOption ? [unassignedOption] : [])];
}
